<template>
  <div class="calculations-wrapper p-3">
    <b-card class="mt-3 mb-3 shadow-sm" id="section-to-print" no-body v-if="calculation">
      <div class="row mx-0 p-3 bg-gray-100 border-bottom d-flex justify-content-between">
        <h5 class="font-weight-bold mb-0 text-gray-700">Dados do Cálculo</h5>
        <div class="self-end">
          <b-form-checkbox
            v-model="calculation.hasJoinedSupplementaryPensionPlan"
            :disabled="!isEditing"
            switch
          >
            Aderiu a Previdência Complementar?
          </b-form-checkbox>
        </div>
      </div>
      <div class="row mx-0">
        <div class="col-md-4 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Nome do Cálculo</h6>
            <div v-if="isEditing">
              <input type="text" class="form-control form-control-sm" v-model="calculation.name" v-on:blur="inputValidation('name')" />
              <ValidationMessage field="name" v-bind:validations="validations" />
            </div>
            <TextInfo :isLoading="isLoading" :text="calculation.name" v-else />
          </div>
        </div>
        <div class="col-md-4 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Dt. Criação</h6>
            <TextInfo :isLoading="isLoading" :text="calculation.createdAt" />
          </div>
        </div>
        <div class="col-md-4 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Dt. Para Atualização</h6>
            <div v-if="isEditing">
              <InputDatePicker v-model="calculation.calculationDate" v-on:blur="inputValidation('calculationDate')" :ignoreLimit="true" />
              <ValidationMessage field="calculationDate" v-bind:validations="validations" />
            </div>
            <TextInfo :isLoading="isLoading" :text="calculation.calculationDate" v-else />
          </div>
        </div>
      </div>
      <div class="row mx-0">
        <div class="col-md-3 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Dt. Ingresso no Serv. Público</h6>
            <div v-if="isEditing">
              <InputDatePicker v-model="calculation.publicServiceIngressDate" v-on:blur="inputValidation('publicServiceIngressDate')" />
              <ValidationMessage field="publicServiceIngressDate" v-bind:validations="validations" />
            </div>
            <TextInfo
              :isLoading="isLoading"
              :text="publicServiceIngressDate"
              :tooltipInfo="isEditing && workRecords.length ? workRecordMessage : ''"
              v-else
            />
          </div>
        </div>
        <div class="col-md-3 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Dt. Ingresso no Regime Atual</h6>
            <div v-if="isEditing">
              <InputDatePicker v-model="calculation.currentRegimeIngressDate" v-on:blur="inputValidation('currentRegimeIngressDate')" />
              <ValidationMessage field="currentRegimeIngressDate" v-bind:validations="validations" />
            </div>
            <TextInfo :isLoading="isLoading" :text="calculation.currentRegimeIngressDate" v-else />
          </div>
        </div>
        <div class="col-md-3 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Regime de Previdência RPPS</h6>
            <div v-if="isEditing">
              <autocomplete 
                v-if="calculation.regimeType !== 'RGPS'"
                :search="searchRegime"
                :get-result-value="getRegimeValue"
                @submit="handleRegimeSubmit"
                :debounce-time="750"
                :default-value="defaultRegime"
              >
              </autocomplete>
              <input 
                v-else
                type="text" 
                class="form-control form-control-sm" 
                value="INSS" 
                disabled 
              />
              <ValidationMessage field="regime" v-bind:validations="validations" />
            </div>
            <TextInfo :isLoading="isLoading" :text="regimeName" v-else />
          </div>
        </div>
        <div class="col-md-3 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Dt. Filiação no RGPS</h6>
            <div v-if="isEditing">
              <InputDatePicker v-model="calculation.registrationDate" v-on:blur="inputValidation('registrationDate')" />
              <ValidationMessage field="registrationDate" v-bind:validations="validations" />
            </div>
            <TextInfo :isLoading="isLoading" :text="calculation.registrationDate" v-else />
          </div>
        </div>

        <div class="col-md-12 px-0 border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Projeção de salários futuros (regras futuras)</h6>
            <b-alert variant="info" show class="small py-2 mb-2">
              <strong>Atenção:</strong> A projeção de salários futuros (regras futuras) aplica-se apenas às <strong>Regras RGPS</strong>. Para Regras RPPS o cálculo do benefício utiliza apenas <strong>Repetir última contribuição</strong>.
            </b-alert>
            <p class="small text-muted mb-2">Selecione como projetar contribuições até a aposentadoria. Pode marcar mais de uma opção para comparar benefícios.</p>
            <div v-if="isEditing" class="d-flex flex-column">
              <div
                v-for="opt in futureSalaryTypeOptions"
                :key="opt.value"
                class="d-flex align-items-center mb-2"
              >
                <b-form-checkbox
                  v-model="futureSalaryTypesModel"
                  :value="opt.value"
                  class="mb-0 mr-2"
                >
                  {{ opt.text }}
                </b-form-checkbox>
                <template v-if="opt.value === 'user_input' && futureSalaryTypesModel.includes('user_input')">
                  <label class="mb-0 mr-2 small text-nowrap">Valor (R$):</label>
                  <b-form-input
                    v-model.number="calculation.userInputFutureSalary"
                    type="number"
                    min="0"
                    step="0.01"
                    size="sm"
                    style="max-width: 140px"
                    placeholder="0,00"
                  />
                </template>
              </div>
            </div>
            <div v-else class="small">
              <template v-if="calculation.futureSalaryTypes && calculation.futureSalaryTypes.length">
                <div
                  v-for="t in (calculation.futureSalaryTypes || [])"
                  :key="t"
                  class="mb-1"
                >
                  {{ (futureSalaryTypeOptions.find(o => o.value === t) || {}).text || t }}
                  <span v-if="t === 'user_input' && calculation.userInputFutureSalary != null" class="text-muted">
                    (valor informado: R$ {{ (calculation.userInputFutureSalary || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }})
                  </span>
                </div>
              </template>
              <span v-else class="text-muted">Repetir última contribuição</span>
            </div>
          </div>
        </div>

        <!-- <div class="col-md-6 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tipo de Regime</h6>
            <div v-if="isEditing">
              <b-form-select
                v-model="calculation.regimeType"
                :options="[
                  { value: 'RPPS', text: 'RPPS' },
                  { value: 'RGPS', text: 'RGPS' },
                  { value: 'RPPSV2', text: 'RPPS V2' }
                ]"
                class="form-control-sm"
                v-on:blur="inputValidation('regimeType')"
              />
              <ValidationMessage field="regimeType" v-bind:validations="validations" />
            </div>
            <TextInfo :isLoading="isLoading" :text="calculation.regimeType" v-else />
          </div>
        </div> -->

      </div>
      <div class="row mx-0" v-if="isEditing">
        <div class="col-md-12 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Observação</h6>
              <input type="text" class="form-control form-control-sm" v-model="calculation.observation" />
          </div>
        </div>
      </div>
      <div class="row mx-0"> 
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo de Serviço Público</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.publicServiceTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="publicServiceTime.years"
                  :tooltipInfo="isEditing && publicServiceTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.publicServiceTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="publicServiceTime.months"
                  :tooltipInfo="isEditing && publicServiceTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.publicServiceTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="publicServiceTime.days"
                  :tooltipInfo="isEditing && publicServiceTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="publicServiceTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo de Magistério</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.teachingTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="teachingTime.years"
                  :tooltipInfo="isEditing && teachingTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.teachingTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="teachingTime.months"
                  :tooltipInfo="isEditing && teachingTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.teachingTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="teachingTime.days"
                  :tooltipInfo="isEditing && teachingTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="teachingTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo de Carreira</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.carreerTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="carreerTime.years"
                  :tooltipInfo="isEditing && carreerTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.carreerTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="carreerTime.months"
                  :tooltipInfo="isEditing && carreerTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.carreerTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="carreerTime.days"
                  :tooltipInfo="isEditing && carreerTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="carreerTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo no Cargo</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.positionTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="positionTime.years"
                  :tooltipInfo="isEditing && positionTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.positionTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="positionTime.months"
                  :tooltipInfo="isEditing && positionTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.positionTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="positionTime.days"
                  :tooltipInfo="isEditing && positionTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="positionTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo de Contribuição</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.contributionTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="contributionTime.years"
                  :tooltipInfo="isEditing && contributionTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.contributionTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="contributionTime.months"
                  :tooltipInfo="isEditing && contributionTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.contributionTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="contributionTime.days"
                  :tooltipInfo="isEditing && contributionTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="contributionTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo de Contribuição <span style="font-size: x-small;">(sem tempo especial)</span></h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.contributionTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="contributionTimeNoSpecialTime.years"
                  :tooltipInfo="isEditing && contributionTimeNoSpecialTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.contributionTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="contributionTimeNoSpecialTime.months"
                  :tooltipInfo="isEditing && contributionTimeNoSpecialTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.contributionTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="contributionTimeNoSpecialTime.days"
                  :tooltipInfo="isEditing && contributionTimeNoSpecialTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="contributionTime" v-bind:validations="validations" /> -->
          </div>
        </div>
      </div>
      <div class="row mx-0" v-if="showMore">
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Especial</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.specialTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime.years"
                  :tooltipInfo="isEditing && specialTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.specialTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime.months"
                  :tooltipInfo="isEditing && specialTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.specialTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime.days"
                  :tooltipInfo="isEditing && specialTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="specialTime" v-bind:validations="validations" /> -->
          </div>
        </div>

        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Especial <span style="font-size: x-small;">(15 anos)</span></h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime15Years.years"
                  :tooltipInfo="isEditing && specialTime15Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime15Years.months"
                  :tooltipInfo="isEditing && specialTime15Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime15Years.days"
                  :tooltipInfo="isEditing && specialTime15Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="specialTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Especial <span style="font-size: x-small;">(20 anos)</span></h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime20Years.years"
                  :tooltipInfo="isEditing && specialTime20Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime20Years.months"
                  :tooltipInfo="isEditing && specialTime20Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime20Years.days"
                  :tooltipInfo="isEditing && specialTime20Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="specialTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Especial <span style="font-size: x-small;">(25 anos)</span></h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime25Years.years"
                  :tooltipInfo="isEditing && specialTime25Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime25Years.months"
                  :tooltipInfo="isEditing && specialTime25Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="specialTime25Years.days"
                  :tooltipInfo="isEditing && specialTime25Years ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="specialTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Especial <span style="font-size: x-small;">(Predominante - {{ mainSpecialTime.mainSpecialLevel }})</span></h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="mainSpecialTime.years"
                  :tooltipInfo="isEditing && mainSpecialTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="mainSpecialTime.months"
                  :tooltipInfo="isEditing && mainSpecialTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="mainSpecialTime.days"
                  :tooltipInfo="isEditing && mainSpecialTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="specialTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
        </div>

        <!-- <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Comum</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="comumContributionTime.years"
                  :tooltipInfo="isEditing && comumContributionTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="comumContributionTime.months"
                  :tooltipInfo="isEditing && comumContributionTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="comumContributionTime.days"
                  :tooltipInfo="isEditing && comumContributionTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
          </div>
        </div> -->

        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Com Deficiência</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.disabledTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledTime.years"
                  :tooltipInfo="isEditing && disabledTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledTime.months"
                  :tooltipInfo="isEditing && disabledTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledTime.days"
                  :tooltipInfo="isEditing && disabledTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="disabledTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Com Deficiência Leve</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.disabledLowTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledLowTime.years"
                  :tooltipInfo="isEditing && disabledLowTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledLowTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledLowTime.months"
                  :tooltipInfo="isEditing && disabledLowTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledLowTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledLowTime.days"
                  :tooltipInfo="isEditing && disabledLowTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="disabledLowTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Com Deficiência Moderada</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.disabledMediumTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledMediumTime.years"
                  :tooltipInfo="isEditing && disabledMediumTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledMediumTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledMediumTime.months"
                  :tooltipInfo="isEditing && disabledMediumTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledMediumTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledMediumTime.days"
                  :tooltipInfo="isEditing && disabledMediumTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="disabledMediumTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Com Deficiência Grave</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.disabledHighTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledHighTime.years"
                  :tooltipInfo="isEditing && disabledHighTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledHighTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledHighTime.months"
                  :tooltipInfo="isEditing && disabledHighTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.disabledHighTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="disabledHighTime.days"
                  :tooltipInfo="isEditing && disabledHighTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="disabledHighTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo De Deficiência <span style="font-size: x-small;">(Predominante - {{ mainDisabledTime.mainDisabledLevelLabel }})</span></h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="mainDisabledTime.years"
                  :tooltipInfo="isEditing && mainDisabledTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="mainDisabledTime.months"
                  :tooltipInfo="isEditing && mainDisabledTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="mainDisabledTime.days"
                  :tooltipInfo="isEditing && mainDisabledTime ? workRecordMessage : ''"
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="specialTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
        </div>

        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Policial</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.policeTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="policeTime.years"
                  :tooltipInfo="isEditing && policeTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.policeTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="policeTime.months"
                  :tooltipInfo="isEditing && policeTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.policeTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="policeTime.days"
                  :tooltipInfo="isEditing && policeTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
              
            <!-- <ValidationMessage field="disabledHighTime" v-bind:validations="validations" /> -->
          </div>
        </div>
        <div class="col-md-2 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Tempo Rural</h6>

            <div class="row">
              <div class="col-6 text-gray-500 font-italic">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="form-control form-control-sm" v-model="calculation.ruralTimeYears" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="ruralTime.years"
                  :tooltipInfo="isEditing && ruralTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Ano(s)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.ruralTimeMonths" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="ruralTime.months"
                  :tooltipInfo="isEditing && ruralTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Mês(es)</div>
            </div>

            <div class="row">
              <div class="col-6">
                <input v-if="isEditing && !workRecords.length" type="number" min="0" class="mt-1 form-control form-control-sm" v-model="calculation.ruralTimeDays" />
                <TextInfo
                  :isLoading="isLoading"
                  emptyText="-"
                  :text="ruralTime.days"
                  :tooltipInfo="isEditing && ruralTime ? workRecordMessage : ''"
                  v-else
                />
              </div>
              <div class="col-6 text-gray-500 font-italic">Dia(s)</div>
            </div>
          </div>
        </div>
        <div class="col-md-4 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Cortes de Data: <span style="font-size: x-small;">{{ limitDate.format("DD/MM/YYYY") }}</span></h6>
            <div>
              <b-form-select
                size="sm"
                id="inline-form-custom-select-pref"
                class="mb-2 mr-sm-2 mb-sm-0 col-sm-12"
                :options="dateOptions"
                v-model="dateOption"
              />
            </div>
          </div>
        </div>
        <div class="col-md-4 px-0 border-right border-bottom border-gray-200">
          <div class="p-3">
            <h6 class="text-uppercase text-gray-600 small">Regime</h6>
            <div>
              <b-form-select
                size="sm"
                id="inline-form-custom-select-pref"
                class="mb-2 mr-sm-2 mb-sm-0 col-sm-12"
                :options="regimeOptions"
                v-model="regimeOption"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="row d-flex justify-content-between mt-2 mb-2 mr-2">
        <div>
          <button class="btn btn-info ml-4" @click="showMore = !showMore">{{ showMore ? 'Esconder' : 'Mostrar mais' }}</button>
        </div>
        <div>
          <router-link
            class="ml-2 btn btn-danger ml-3" title="Fechar" role="button" size="sm"
            v-bind:to="{ name: 'customer', params: { id: customerId } }"
          >
            Fechar
          </router-link>

          <button class="btn btn-warning ml-3" @click="createCalc(calculation)" v-if="isEditing && !calculation.id">Criar</button>
          <button class="btn btn-warning ml-3" @click="updateCalc(calculation)" v-else-if="isEditing && calculation.id">Salvar</button>
          <button class="btn btn-warning ml-3" @click="isEditing = true" v-else>Editar</button>
        </div>
      </div>

      <div class="bg-gray-100 border-bottom d-flex" v-if="calculationId">
        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedTab === 'work-records' }"
          href="#"
          @click.prevent="selectedTab = 'work-records'"
        >
          Registros de tempo
        </a>

        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedTab === 'rpps' }"
          href="#"
          @click.prevent="handleRegimeTypeChange('RPPS')"
        >
          Regras RPPS
        </a>

        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedTab === 'rgps' }"
          href="#"
          @click.prevent="handleRegimeTypeChange('RGPS')"
        >
          Regras RGPS
        </a>

        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedTab === 'compare' }"
          href="#"
          @click.prevent="handleCompareRules"
        >
          Comparar Regras
        </a>
      </div>
      <div class="bg-gray-100 border-bottom d-flex" v-if="calculationId && (selectedTab === 'rpps' || selectedTab === 'rgps')">
        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedSubTab === 'rules' }"
          href="#"
          @click.prevent="selectedSubTab = 'rules'"
        >
          Regras Aplicáveis
        </a>

        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedSubTab === 'future-rules' }"
          href="#"
          @click.prevent="selectedSubTab = 'future-rules'"
        >
          Regras Futuras
        </a>

        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedSubTab === 'rules-not-applicable' }"
          href="#"
          @click.prevent="selectedSubTab = 'rules-not-applicable'"
        >
          Regras Não Aplicáveis
        </a>

        <a
          class="tab-link-item p-3"
          :class="{ 'selected-tab': selectedSubTab === 'genial-report' }"
          href="#"
          @click.prevent="selectedSubTab = 'genial-report'"
        >
          Relatório Genial
        </a>
      </div>

      <div class="d-flex align-items-center justify-content-start mt-2 mr-2" v-if="['rules', 'future-rules'].includes(selectedSubTab) && selectedTab === 'rpps' || selectedTab === 'rgps'">
        <span class="ml-2 text-gray-600 font-weight-bold small">ORDENAR POR:</span>
        <div class="ml-2 btn-group btn-group-toggle" data-toggle="buttons">
          <label class="btn btn-sm btn-outline-secondary" :class="{ active: sortBy === 'date' }">
            <input type="radio" name="options" id="option1" autocomplete="off" :checked="sortBy === 'date'" @change="sortBy = 'date'"> DATA
          </label>
          <label class="btn btn-sm btn-outline-secondary" :class="{ active: sortBy === 'value' }">
            <input type="radio" name="options" id="option2" autocomplete="off" :checked="sortBy === 'value'" @change="sortBy = 'value'"> VALOR
          </label>
        </div>
      </div>

      <component 
        :is="currentComponent" 
        v-if="calculation.id" 
        :key="workRecordsComponentKey"
        :calculationId="calculationId"
        :applicableRules="sortedApplicableRules"
        :futureRules="sortedFutureRules"
        :notApplicableRules="notApplicableRules"
        :loadingRules="loadingRules"
        :workRecords="workRecords"
        :customer="customer"
        :calculation="calculation"
        :regimeDate="regimeDate"
        :hasRegime="hasRegime"
        :regimeType="regimeType"
      />
      <CompareRulesModal 
        ref="compareRulesModal" 
        :calculationId="calculationId"
        :workRecords="workRecords"
        :customer="customer"
        :calculation="calculation"
        :regimeDate="regimeDate"
      />
    </b-card>
  </div>
</template>

<script>
import axios from 'axios';
import { getCalculationById, createCalculation, updateCalculation } from '@/services/calculations';
import { mapGetters, mapActions } from 'vuex';
import ValidationMessage from '@/components/ValidationMessage.vue';
import InputDatePicker from '@/components/InputDatePicker.vue';
import TextInfo from '@/components/TextInfo.vue';
import moment from 'moment';
import { getMainDisabledTimeWithConversion, getMainSpecialTimeWithConversion, getTime, calculateRetiredDate } from '../../../utils/common';
import { getRegimes } from '@/services/regimes';
import Autocomplete from '@trevoreyre/autocomplete-vue';
import CompareRulesModal from './CompareRulesModal.vue';
import WorkRecords from '@/views/customer/work-records/WorkRecords.vue';
import Rules from '@/views/customer/Rules.vue';
import FutureRules from '@/views/customer/FutureRules.vue';
import RulesNotApplicable from '@/views/customer/RulesNotApplicable.vue';
import GenialReport from '@/views/customer/GenialReport.vue';

export default {
  name: 'Calculations',
  components: {
    TextInfo, 
    InputDatePicker, 
    ValidationMessage,
    Autocomplete,
    CompareRulesModal,
    WorkRecords,
    Rules,
    FutureRules,
    RulesNotApplicable,
    GenialReport
  },

  data: () => {
    const initialCalculation = {
      name: null,
      customerId: null,
      state: null,
      regime: null,
      regimeType: 'RPPS',
      publicServiceIngressDate: null,
      registrationDate: null,
      currentRegimeIngressDate: null,
      publicServiceTime: null,
      teachingTimeYears: 0,
      teachingTimeMonths: 0,
      teachingTimeDays: 0,
      carreerTime: null,
      positionTime: null,
      contributionTimeYears: null,
      contributionTimeMonths: null,
      contributionTimeDays: null,
      publicServiceTimeDays: 0,
      publicServiceTimeMonths: 0,
      publicServiceTimeYears: 0,
      carreerTimeDays: 0,
      carreerTimeMonths: 0,
      carreerTimeYears: 0,
      positionTimeDays: 0,
      positionTimeMonths: 0,
      positionTimeYears: 0,
      futureSalaryTypes: ['repeat_last'],
      userInputFutureSalary: null,
    }

    return {
      calculationId: null,
      initialCalculation,
      showCalculationForm: false,
      calculation: {},
      validations: {},
      customer: {},
      isEditing: false,
      isLoading: false,
      workRecordMessage: 'Não é possível alterar esse campo pois o sistema está considerando os dados dos registros de trabalho.',
      showMore: false,
      dateOption: null,
      dateOptions: [
        { text: 'Até Hoje', value: null },
        { text: 'Até 16/12/1998', value: '1998-12-16' },
        { text: 'Até 31/12/2003', value: '2003-12-31' },
        { text: 'Até a Data da Reforma', value: 'regime'},
      ],
      regimeOption: 'ALL',
      regimeOptions: [
        { text: 'RPPS', value: 'RPPS' },
        { text: 'RGPS', value: 'RGPS' },
        { text: 'Todos', value: 'ALL' }
      ],
      regimes: [],
      defaultRegime: '',
      regimeDate: '',
      hasRegime: false,
      regimeType: 'RPPS',
      selectedTab: 'work-records',
      selectedSubTab: 'rules',
      applicableRules: [],
      notApplicableRules: [],
      futureRules: [],
      loadingRules: false,
      currentRulesRequest: null,
      sortBy: 'date', // 'date' | 'value'
      futureSalaryTypeOptions: [
        { value: 'min', text: 'Salário mínimo (na data de atualização)' },
        { value: 'ceiling', text: 'Teto (na data de atualização)' },
        { value: 'user_input', text: 'Valor informado' },
        { value: 'repeat_last', text: 'Repetir última contribuição' },
        { value: 'zero', text: 'Zerados (apenas idade; não aumenta tempo de contribuição)' },
      ],
    }
  },

  created() {
    this.$watch(
      () => this.$route.params,
      (toParams, previousParams) => {
        this.calculationId = toParams.calculationId;
        this.fetchCalculation(toParams.calculationId);
        this.loadWorkRecords(toParams.calculationId);
      }
    )

    if (!this.calculationId) {
      this.calculationId = this.$route.params.calculationId;
      this.fetchCalculation(this.$route.params.calculationId);
      this.loadWorkRecords(this.$route.params.calculationId);
    }
  },

  computed: {
    ...mapGetters('workRecords', ['workRecords']),

    futureSalaryTypesModel: {
      get() {
        const arr = this.calculation && this.calculation.futureSalaryTypes;
        return Array.isArray(arr) ? arr : ['repeat_last'];
      },
      set(val) {
        if (!this.calculation) return;
        const arr = Array.isArray(val) && val.length ? val : ['repeat_last'];
        this.$set(this.calculation, 'futureSalaryTypes', arr);
      },
    },

    filteredWorkRecords() {
      if (this.regimeOption === 'ALL') return this.workRecords;
      return this.workRecords.filter(record => record.regimeType === this.regimeOption);
    },

    customerId() {
      return this.$route.params.id;
    },

    sortedApplicableRules() {
      const rules = [...this.applicableRules].map(rule => {
        if (!rule._calculatedRetiredDate) {
          // Fonte preferida: applicableAt do MOTOR (max de TODAS as aplicabilidades,
          // incl. magistério/especial/polícia). O recompute legado produzia header
          // "Possível aposentadoria em" ANTERIOR a um "Completou em" (smoke 04/07).
          // #524 item (2) — Ju 14/08: o cabeçalho é SEMPRE a data do ÚLTIMO
          // REQUISITO cumprido. `lastRequirementAt` = `applicableAt` sem a
          // substituição artificial do ramo RGPS (vigência do banco trocada pela
          // data do CÁLCULO). Para RPPS os dois são iguais por construção.
          // `applicableAt` fica como fallback (regra vinda de motor antigo) e
          // segue intocado como insumo da precedência da compulsória.
          const retiredDate = rule.lastRequirementAt
            ? moment(rule.lastRequirementAt)
            : rule.applicableAt
              ? moment(rule.applicableAt)
              : this.calculateRetiredDate(rule);
          // Guard #432-7: moment invalido (data corrompida/nao alcancavel) nao pode
          // virar "Invalid date" na tela -- mesmo padrao de sortedFutureRules (:1433).
          rule._calculatedRetiredDate = (retiredDate && retiredDate.isValid && retiredDate.isValid())
            ? retiredDate.format('DD/MM/YYYY')
            : null;
        }
        return rule;
      });
      if (this.sortBy === 'date') {
        return rules.sort((a, b) => {
          const dateA = a._calculatedRetiredDate ? moment(a._calculatedRetiredDate, 'DD/MM/YYYY') : moment(0);
          const dateB = b._calculatedRetiredDate ? moment(b._calculatedRetiredDate, 'DD/MM/YYYY') : moment(0);
          return dateA - dateB;
        });
      } else if (this.sortBy === 'value') {
        return rules.sort((a, b) => (b.estimatedBenefit || 0) - (a.estimatedBenefit || 0));
      }
      return rules;
    },

    sortedFutureRules() {
      const rules = [...this.futureRules].map(rule => {
        if (!rule._calculatedRetiredDate) {
          const retiredDate = rule.whenToRetire ? moment(rule.whenToRetire) : this.calculateRetiredDate(rule);
          rule._calculatedRetiredDate = (retiredDate && retiredDate.isValid && retiredDate.isValid()) ? retiredDate.format('DD/MM/YYYY') : null;
        }
        return rule;
      });
      if (this.sortBy === 'date') {
        return rules.sort((a, b) => {
          const dateA = a._calculatedRetiredDate ? moment(a._calculatedRetiredDate, 'DD/MM/YYYY') : moment(0);
          const dateB = b._calculatedRetiredDate ? moment(b._calculatedRetiredDate, 'DD/MM/YYYY') : moment(0);
          return dateA - dateB;
        });
      } else if (this.sortBy === 'value') {
        return rules.sort((a, b) => (b.estimatedBenefit || 0) - (a.estimatedBenefit || 0));
      }
      return rules;
    },



    regimeName() {
      if (this.calculation && this.calculation.regime) {
        return this.calculation.regime.name || this.calculation.regime;
      }

      return '';
    },

    publicServiceIngressDate() {
      const { calculation, filteredWorkRecords } = this;

      if (calculation.publicServiceIngressDate) {
        return calculation.publicServiceIngressDate;
      }

      return null;

      // if (!workRecords.length) {
        // return calculation.publicServiceIngressDate;
      // }

      // const [date] = workRecords.map((workRecord) => workRecord.ingressDate).sort((a, b) => new Date(a) - new Date(b));
      // return moment(date).format('DD/MM/YYYY');
    },

    limitDate() {
      if (this.dateOption) {
        if (this.dateOption === "regime") {
          if (this.regimeOption === 'RGPS') return moment('2019-11-13');
          return moment(this.regimeDate);
        }
        const date = moment(this.dateOption);
        return date;
      }
      return moment();
    },

    publicServiceTime() {

      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: calculation.publicServiceTimeYears,
          months: calculation.publicServiceTimeMonths,
          days: calculation.publicServiceTimeDays,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isPublicService', gender, this.regimeDate);
    },

    teachingTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: calculation.teachingTimeYears,
          months: calculation.teachingTimeMonths,
          days: calculation.teachingTimeDays,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isTeaching', gender, this.regimeDate);
    },

    carreerTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: calculation.carreerTimeYears,
          months: calculation.carreerTimeMonths,
          days: calculation.carreerTimeDays,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isCurrentCareer', gender, this.regimeDate);
    },

    positionTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: calculation.positionTimeYears,
          months: calculation.positionTimeMonths,
          days: calculation.positionTimeDays,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isCurrentPosition', gender, this.regimeDate);
    },

    contributionTimeNoSpecialTime() {
      const { customer, filteredWorkRecords } = this;

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, null, gender, this.regimeDate, true);
    },

    contributionTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: calculation.contributionTimeYears,
          months: calculation.contributionTimeMonths,
          days: calculation.contributionTimeDays,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, null, gender, this.regimeDate);
    },

    specialTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isSpecialTime', gender, this.regimeDate);
    },

    specialTime15Years() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isSpecialTime-15 anos', gender, this.regimeDate);
    },

    specialTime20Years() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isSpecialTime-20 anos', gender, this.regimeDate);
    },

    specialTime25Years() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isSpecialTime-25 anos', gender, this.regimeDate);
    },

    mainSpecialTime() {
      const response = getMainSpecialTimeWithConversion(this.specialTime15Years, this.specialTime20Years, this.specialTime25Years);
      
      return response;
    },

    disabledTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isDisabled', gender, this.regimeDate);
    },

    disabledLowTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isDisabled-low', gender, this.regimeDate);
    },

    disabledMediumTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isDisabled-medium', gender, this.regimeDate);
    },

    disabledHighTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isDisabled-high', gender, this.regimeDate);
    },

    comumContributionTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'comum', gender, this.regimeDate, true);
    },

    mainDisabledTime() {
      const response = getMainDisabledTimeWithConversion(this.customer.gender, this.disabledLowTime, this.disabledMediumTime, this.disabledHighTime, this.comumContributionTime);
      
      return response;
    },

    policeTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isPoliceTime', gender, this.regimeDate);
    },

    ruralTime() {
      const { customer, calculation, filteredWorkRecords, workRecords } = this;

      if (!workRecords) {
        return {
          years: 0,
          months: 0,
          days: 0,
        }
      }

      const gender = customer.gender;

      return getTime(filteredWorkRecords, this.limitDate, 'isRuralTime', gender, this.regimeDate);
    },

    workRecordsComponentKey() {
      const c = this.calculation || {};
      const flag = c.hasJoinedSupplementaryPensionPlan ?? c.hasJoinedSupplemntaryPensionPlan ?? false;
      return `${c.id ?? ''}-${flag}`;
    },

    currentComponent() {
      if (this.selectedTab === 'work-records') {
        return 'WorkRecords';
      }
      
      if (this.selectedTab === 'rpps' || this.selectedTab === 'rgps') {
        switch (this.selectedSubTab) {
          case 'rules':
            return 'Rules';
          case 'future-rules':
            return 'FutureRules';
          case 'rules-not-applicable':
            return 'RulesNotApplicable';
          case 'genial-report':
            return 'GenialReport';
          default:
            return 'Rules';
        }
      }
      
      return null;
    }
  },

  methods: {
    ...mapActions('workRecords', ['loadWorkRecords']),
    ...mapActions('calculations', ['loadCalculations']),

    calculateRetiredDate(rule) {
      if (!rule) return null;
      
      const result = calculateRetiredDate(rule);
      if (result && result !== false) {
        return moment(result, 'DD/MM/YYYY');
      }
      
      return null;
    },
   
    async searchRegime(input) {
      try {
        this.regimes = await getRegimes({ name: input });
      } catch (err) {}

      return this.regimes;
    },

    getRegimeValue(result) {
      return result.name;
    },

    handleRegimeSubmit(result) {
      this.calculation.regime = result.name;
      this.calculation.regimeId = result.id;
    },

    async createCalc() {
      this.isLoading = true;

      this.calculation.customerId = this.customerId;

      const { calculation } = await createCalculation(this.calculation);

      this.calculation = calculation;

      this.isEditing = false;
      this.isLoading = false;

      await this.loadCalculations(this.customerId);
    },

    async updateCalc() {
      if (this.formValidation()) {
        this.isLoading = true;
        this.isEditing = false;

        this.calculation.customerId = this.customerId;
        const types = Array.isArray(this.futureSalaryTypesModel) ? this.futureSalaryTypesModel : ['repeat_last'];
        this.calculation.futureSalaryTypes = JSON.parse(JSON.stringify(types));
        this.calculation.userInputFutureSalary =
          this.calculation.userInputFutureSalary != null && this.calculation.userInputFutureSalary !== ''
            ? Number(this.calculation.userInputFutureSalary)
            : null;

        const { calculation } = await updateCalculation(this.calculationId, this.calculation);

        await this.fetchCalculation(calculation.id);
        await this.loadWorkRecords(calculation.id);
        await this.loadCalculations(this.customerId);

        const target = { name: 'work-records', params: { calculationId: calculation.id } };

        if (this.$route.name !== target.name ||
            String(this.$route.params.calculationId) !== String(target.params.calculationId)) {
          await this.$router.replace(target);
        }

        // this.$router.push({ 
        //   name: 'work-records', 
        //   params: { 
        //     calculationId: calculation.id 
        //   }
        // });
      }
    },

    inputValidation(field) {
      let isValid = true;
      const { validations } = this;
      const isNormalField = !['contributionTime', 'teachingTime'].includes(field);
      
      if (isNormalField) {
        const value = String(this.calculation[field] || '').replace('Data inválida', '');

        if (!value) {
          validations[field] = 'Campo obrigatório';
          isValid = false;
        } else {
          validations[field] = '';
        }

        if (field === 'email') {
          const emailRegex = /\S+@\S+\.\S+/;
          if (value && !emailRegex.test(value)) {
            validations.email = 'E-mail inválido';
            isValid = false;
          }
        }

        if (field === 'name') {
          if (value && value.length > 27) {
            validations[field] = 'Nome muito longo.'
          }
        }

        if (field === 'regimeId') {
          if (value && Number.isNaN(+value)) {
            validations[field] = 'Regime inválido.'
          }
        }

        if (field === 'regimeType') {
          if (!['RPPS', 'RGPS'].includes(value)) {
            validations[field] = 'Tipo de regime inválido';
            isValid = false;
          }
        }
      }

      // if (field = 'contributionTime') {
      //   if (!this.calculation.contributionTimeYears && !this.calculation.contributionTimeMonths && !this.calculation.contributionTimeDays) {
      //     validations.contributionTime = 'Campo obrigatório';
      //     isValid = false;
      //   } else {
      //     validations.contributionTime = '';
      //   }
      // }

      // if (field = 'teachingTime') {
      //   if (!this.calculation.teachingTimeYears && !this.calculation.teachingTimeMonths && !this.calculation.teachingTimeDays) {
      //     validations.teachingTime = 'Campo obrigatório';
      //     isValid = false;
      //   } else {
      //     validations.teachingTime = '';
      //   }
      // }

      // if (field = 'carreerTime') {
      //   if (!this.calculation.carreerTimeYears && !this.calculation.carreerTimeMonths && !this.calculation.carreerTimeDays) {
      //     validations.carreerTime = 'Campo obrigatório';
      //     isValid = false;
      //   } else {
      //     validations.carreerTime = '';
      //   }
      // }

      // if (field = 'publicServiceTime') {
      //   if (!this.calculation.publicServiceTimeYears && !this.calculation.publicServiceTimeMonths && !this.calculation.publicServiceTimeDays) {
      //     validations.publicServiceTime = 'Campo obrigatório';
      //     isValid = false;
      //   } else {
      //     validations.publicServiceTime = '';
      //   }
      // }

      // if (field = 'positionTime') {
      //   if (!this.calculation.positionTimeYears && !this.calculation.positionTimeMonths && !this.calculation.positionTimeDays) {
      //     validations.positionTime = 'Campo obrigatório';
      //     isValid = false;
      //   } else {
      //     validations.positionTime = '';
      //   }
      // }

      this.validations = { ...validations };
      return isValid;
    },

    formValidation() {
      let isValid = true;
      const fields = [
        // 'regime'
      ]

      fields.forEach((field) => {
        if (!this.inputValidation(field)) {
          isValid = false;
        }
      });

      return isValid;
    },

    fetchWorkRecords() {
      this.loadWorkRecords(this.$route.params.id);
    },

    fetchUpdateIndex() {
      getUpdateIndex()
        .then((response) => {
          this.updateIndex = response.updateIndex;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async fetchCalculation(calculationId) {
      this.isLoading = true;

      const { calculation, customer, regimeDate, regime, hasRegime } = await getCalculationById(calculationId);

      this.customer = customer;
      this.calculation = calculation;

      this.calculation.publicServiceIngressDate = moment(calculation.publicServiceIngressDate).utc().format('DD/MM/YYYY');
      this.calculation.calculationDate = calculation.calculationDate ? moment(calculation.calculationDate).utc().format('DD/MM/YYYY') : moment(calculation.updatedAt).utc().format('DD/MM/YYYY') ;
      this.calculation.createdAt = moment(calculation.createdAt).utc().format('DD/MM/YYYY');
      this.calculation.registrationDate = calculation.registrationDate ? moment(calculation.registrationDate).utc().format('DD/MM/YYYY') : null;
      this.calculation.currentRegimeIngressDate = calculation.currentRegimeIngressDate ? moment(calculation.currentRegimeIngressDate).utc().format('DD/MM/YYYY') : null;

      this.calculation.futureSalaryTypes = Array.isArray(calculation.futureSalaryTypes) && calculation.futureSalaryTypes.length
        ? calculation.futureSalaryTypes
        : ['repeat_last'];
      this.calculation.userInputFutureSalary = calculation.userInputFutureSalary != null ? Number(calculation.userInputFutureSalary) : null;

      this.defaultRegime = calculation.regime;
      this.regimeDate = regimeDate;
      this.hasRegime = hasRegime
      this.isLoading = false;
    },

    handlerAddRemuneration(record) {
      this.recordSelected = record;
      this.showRemunerationFormModal = true;
    },

    handlerEditWorkRecord(record) {
      this.recordSelected = record;
      this.showCalculationForm = true;
    },

    async handlerRemoveWorkRecord(record) {
      const result = await this.$swal.fire({
        title: 'Tem certeza que deseja remover esse registro?',
        text: 'Esta ação não poderá ser desfeita!',
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Sim, pode remover!'
      });

      if (!result.isConfirmed) { return; }

      try {
        await axios.delete(`/calculations/${record.id}`);

        this.$swal.fire(
          'Pronto!',
          `Removido com sucesso!`,
          'success'
        )
        await this.fetchWorkRecords();
      } catch (error) {
        this.$swal.fire(
          'Ops!',
          `Não foi possível remover, tente novamente mais tarde!`,
          'error'
        )
      }
    },

    handlerCloseModal() {
      this.showCalculationForm = false;
      this.recordSelected = null;
    },

    async handleRegimeTypeChange(type) {
      if (this.currentRulesRequest) {
        this.currentRulesRequest.cancel('Request cancelled due to new tab selection');
      }

      this.regimeType = type;
      this.selectedTab = type.toLowerCase();
      this.loadingRules = true;
      
      this.applicableRules = [];
      this.notApplicableRules = [];
      this.futureRules = [];
      
      const CancelToken = axios.CancelToken;
      const source = CancelToken.source();
      this.currentRulesRequest = source;
      
      try {
        const { data } = await axios.get(`/calculations/${this.calculationId}/rules`, {
          params: { regimeType: type },
          cancelToken: source.token
        });

        if (this.currentRulesRequest === source) {
          this.applicableRules = data.applicableRules || [];
          this.notApplicableRules = data.notApplicableRules || [];
          this.futureRules = data.futureRules || [];
          this.regimeDate = data.regimeDate;
        }
      } catch (err) {
        // console.log(err);
      } finally {
        if (this.currentRulesRequest === source) {
          this.loadingRules = false;
          this.currentRulesRequest = null;
        }
      }
    },

    handleCompareRules() {
      this.$refs.compareRulesModal.open();
    },
  },

  watch: {

  },
}
</script>

<style>
  .autocomplete-input {
    border: 1px solid #eee;
    /* border-radius: 8px; */
    width: 100%;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    padding-left: 0.5rem;
    /* padding: 12px 12px 12px 48px; */
    box-sizing: border-box;
    position: relative;
    font-size: 16px;
    line-height: 1.5;
    flex: 1;
    /* background-color: #eee; */
    /* background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjNjY2IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+"); */
    /* background-repeat: no-repeat; */
    /* background-position: 12px */
  }

  .autocomplete-input:focus,
  .autocomplete-input[aria-expanded=true] {
    border-color: rgba(0, 0, 0, .12);
    background-color: #fff;
    outline: none;
    box-shadow: 0 2px 2px rgba(0, 0, 0, .16)
  }

  [data-position=below] .autocomplete-input[aria-expanded=true] {
    border-bottom-color: transparent;
    border-radius: 8px 8px 0 0
  }

  [data-position=above] .autocomplete-input[aria-expanded=true] {
    border-top-color: transparent;
    border-radius: 0 0 8px 8px;
    z-index: 2
  }

  .autocomplete[data-loading=true]:after {
    content: "";
    border: 3px solid rgba(0, 0, 0, .12);
    border-right-color: rgba(0, 0, 0, .48);
    border-radius: 100%;
    width: 20px;
    height: 20px;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    animation: rotate 1s linear infinite
  }

  .autocomplete-result-list {
    margin: 0;
    border: 1px solid rgba(0, 0, 0, .12);
    padding: 0;
    box-sizing: border-box;
    max-height: 296px;
    overflow-y: auto;
    background: #fff;
    list-style: none;
    box-shadow: 0 2px 2px rgba(0, 0, 0, .16)
  }

  [data-position=below] .autocomplete-result-list {
    margin-top: -1px;
    border-top-color: transparent;
    border-radius: 0 0 8px 8px;
    padding-bottom: 8px
  }

  [data-position=above] .autocomplete-result-list {
    margin-bottom: -1px;
    border-bottom-color: transparent;
    border-radius: 8px 8px 0 0;
    padding-top: 8px
  }

  .autocomplete-result {
    cursor: default;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    padding-left: 0.5rem;
    /* padding: 12px 12px 12px 48px; */
    /* background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjY2NjIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCI+PGNpcmNsZSBjeD0iMTEiIGN5PSIxMSIgcj0iOCIvPjxwYXRoIGQ9Ik0yMSAyMWwtNC00Ii8+PC9zdmc+"); */
    /* background-repeat: no-repeat; */
    /* background-position: 12px */
  }

  .autocomplete-result:hover,
  .autocomplete-result[aria-selected=true] {
    background-color: rgba(0, 0, 0, .06)
  }

  @keyframes rotate {
    0% {
      transform: translateY(-50%) rotate(0deg)
    }

    to {
      transform: translateY(-50%) rotate(359deg)
    }
  }

  .tab-link-item {
    color: #6c757d;
    text-decoration: none;
    border-bottom: 2px solid transparent;
  }

  .tab-link-item:hover {
    color: #495057;
    text-decoration: none;
  }

  .tab-link-item.selected-tab {
    color: #007bff;
    border-bottom: 2px solid #007bff;
  }
</style>
